<template>
  <b-modal id="modal-punch-card-campaign-reward-modal" :title="form.is_main ? 'Set your Main Reward' : 'Add a Reward'"
    centered @show="initForm" @hide="clearModal">
    <b-form>
      <b-form-group label-class="punch-card-form-label" :label="form.is_main ? 'Main Reward Label' : 'Milestone Label'">
        <b-form-input id="input-punch-card-campaign-reward-milestone-label" v-model="form.milestone_label"
          :maxlength="maxLengths.milestone_label" :state="!$v.milestone_label.$errors.length"
          :class="{ 'border-danger': $v.milestone_label.$errors.length }"></b-form-input>
        <b-form-invalid-feedback :force-show="$v.milestone_label.$errors.length > 0">
          <p class="mb-0" v-for="error in $v.milestone_label.$errors" :key="error.$uid">{{ error.$message }}</p>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label-class="punch-card-form-label" label="Reward">
        <b-dropdown id="dropdown-punch-card-campaign-rewards" :text="form.reward ? form.reward.name : 'Select a reward'"
          :toggle-class="['text-dark text-left shadow-none', { 'unselected': !form.reward, 'border-danger': $v.milestone_label.$errors.length }]"
          menu-class="w-100 p-0" class="w-100" no-caret>
          <div v-infinite-scroll="loadMoreRewards" :infinite-scroll-disabled="rewardsListLoading"
            :infinite-scroll-immediate-check="true" infinite-scroll-distance="50">
            <template v-if="punchCardRewardsList.length > 0">
              <b-dropdown-item v-for="({ name, uuid }, index) in punchCardRewardsList"
                :key="`option-punch-card-campaign-rewards-${index}`" :id="`option-punch-card-campaign-rewards-${index}`"
                @click="form.reward = { name, uuid }">
                {{ name }}
              </b-dropdown-item>
            </template>
            <div v-if="rewardsListLoading" class="text-muted p-3 text-center">Loading...</div>
          </div>
        </b-dropdown>
        <b-form-invalid-feedback :force-show="$v.reward.$errors.length > 0">
          <p class="mb-0" v-for="error in $v.reward.$errors" :key="error.$uid">{{ error.$message }}</p>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group v-if="form.is_main" label-class="punch-card-form-label" label="Main Reward Image">
        <ImageUpload ref="newRewardImgUpload" class="mb-4 pb-1" :extensions="['.jpg', '.png']"
          recommendation="Accepts .jpg, .png." :enableCropping="true" @onPreviewImage="setImage"
          @onClearImage="clearImage" :resetImageUploader="resetImageUploader" @hasWarning="setWarningMessage"
          :showWarningMessage="false" :isThumbNail="true" :imgUploadId="`rewardImageUploader-${source}`"
          :key="`rewardImageUploader-${source}`" :imgRaw="form.reward_image" />
        <b-form-invalid-feedback :force-show="$v.reward_image.$errors.length > 0 || !!uploadedImgWarning">
          <template v-if="!!uploadedImgWarning">{{ uploadedImgWarning }}</template>
          <template v-else>
            <p class="mb-0" v-for="error in $v.reward_image.$errors" :key="error.$uid">{{ error.$message }}</p>
          </template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group v-else label-class="punch-card-form-label">
        <template slot="label">
          <div class="d-inline-flex align-items-center">
            <span class="font-weight-bold">After</span>
            <div class="d-inline-flex align-items-center position-relative">
              <b-form-input id="input-punch-card-campaign-reward-stamp-count" v-model="form.stamps_count" min="1"
                :max="campaignData.design.number_of_stamps - 1" type="number" step="1" class="mx-2 pr-3"
                ref="punchCardRewardStampsCount"></b-form-input>
              <div class="input-controls position-absolute d-inline-flex flex-column h-100">
                <b-button class="value-control -up"
                  @click="controlValue('stamps_count', 'up', 'punchCardRewardStampsCount')"></b-button>
                <b-button class="value-control -down"
                  @click="controlValue('stamps_count', 'down', 'punchCardRewardStampsCount')"></b-button>
              </div>
            </div>
            <span class="font-weight-bold">stamps</span>
          </div>
        </template>
        <b-form-invalid-feedback :force-show="$v.stamps_count.$errors.length > 0">
          <p class="mb-0" v-for="error in $v.stamps_count.$errors" :key="error.$uid">{{ error.$message }}</p>
        </b-form-invalid-feedback>
      </b-form-group>
    </b-form>
    <template slot="modal-footer">
      <b-button variant="primary" @click="validateForm">{{ mode === "create" ? "Create" : "Update" }}</b-button>
    </template>
  </b-modal>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from "@vue/composition-api";
import useVuelidate from "@vuelidate/core";
import { helpers, minValue, maxValue, requiredIf } from "@vuelidate/validators";
import { fieldRequiredErrorMessage } from "../../punch.card.utilities";
import { mapFields } from "vuex-map-fields";
import ImageUpload from "@/components/common/ImageUpload.vue";
const infiniteScroll = require("vue-infinite-scroll");

export default defineComponent({
  props: {
    source: {
      type: String,
      default: "create"
    }
  },
  components: { ImageUpload },
  computed: {
    ...mapFields("punchCard", ["campaignData"])
  },
  directives: { infiniteScroll },
  setup(_, { root }) {
    const currentInstance = getCurrentInstance();

    const maxLengths = {
      milestone_label: 50
    };

    const isMainReward = computed(() => !hasMainReward.value);

    const hasMainReward = computed(() => currentInstance.proxy.campaignData.design.milestones.map(({ is_main }) => is_main).reduce((res, cur) => res || cur, false));

    const punchCardRewardsList = computed(() => root.$store.state.punchCard.punchCardRewardsList || []);

    const form = ref({
      milestone_label: null,
      reward: null,
      stamps_count: null,
      reward_image: null,
      is_main: false
    });

    const resetFormValue = () => {
      form.value = {
        milestone_label: null,
        reward: null,
        stamps_count: null,
        reward_image: null,
        is_main: isMainReward.value
      };
    };

    const initForm = () => {
      resetFormValue();

      $v.value.$reset();
      resetImageUploader.value = false;
      uploadedImgWarning.value = null;
      modalOpen.value = true;

      if (!rewardsListLoading.value) getPunchCardRewardsList();

      if (mode.value === "create") resetImgWarning();
    };

    const maxStamps = computed(() => root.$store.state.punchCard.campaignData.design.number_of_stamps);

    const rules = computed(() => ({
      milestone_label: {
        required: helpers.withMessage(fieldRequiredErrorMessage, requiredIf(() => modalOpen.value))
      },
      reward: {
        required: helpers.withMessage(fieldRequiredErrorMessage, requiredIf(() => modalOpen.value))
      },
      reward_image: {
        required: helpers.withMessage(fieldRequiredErrorMessage, requiredIf(() => modalOpen.value && form.value.is_main))
      },
      stamps_count: {
        required: helpers.withMessage(fieldRequiredErrorMessage, requiredIf(() => modalOpen.value && !form.value.is_main)),
        minValue: minValue(1),
        maxValue: maxValue(maxStamps.value - 1),
        unique: helpers.withMessage("Stamp number is already in use.",
          () => !helpers.req(form.value.stamps_count && !form.value.is_main) || !isSelfExisting.value)
      }
    }));

    const isSelfExisting = computed(() => {
      const existingStampsCount = currentInstance.proxy.campaignData.design.milestones.map(({ stamps_count }) => Number(stamps_count)).filter((item) => item) || [];

      const thisExistingStampCount = currentInstance.proxy.campaignData.design.milestones.find(({ stamps_count }) => Number(stamps_count) === Number(form.value.stamps_count));

      const standardRules = () => existingStampsCount.length && existingStampsCount.includes(Number(form.value.stamps_count));

      if (mode.value === "create")
        return standardRules()
      else if (mode.value === "update")
        return standardRules() && currentInstance.proxy.campaignData.design.milestones.indexOf(thisExistingStampCount) !== updateIndex.value;
    });

    const $v = useVuelidate(rules, form);

    const validateForm = () => {
      $v.value.$touch();

      if ($v.value.$dirty && !$v.value.$invalid) {
        const formValue = { ...form.value, stamps_count: form.value.stamps_count ? Number(form.value.stamps_count) : form.value.stamps_count };

        if (mode.value === "create") {
          if (isMainReward.value)
            currentInstance.proxy.campaignData.design.milestones.splice(0, 0, formValue);
          else
            currentInstance.proxy.campaignData.design.milestones.push(formValue);
        } else currentInstance.proxy.campaignData.design.milestones[updateIndex.value] = formValue;

        root.$emit("forceUpdate");
        root.$emit("bv::hide::modal", "modal-punch-card-campaign-reward-modal");
      }
    };

    const setImage = (image) => {
      form.value.reward_image = image;
    };
    const clearImage = () => {
      form.value.reward_image = null
    };

    const uploadedImgWarning = ref(null);
    const resetImageUploader = ref(false);

    const setWarningMessage = (message) => {
      uploadedImgWarning.value = message;
      resetImageUploader.value = false;
    };
    const resetImgWarning = () => {
      uploadedImgWarning.value = null;
      resetImageUploader.value = true;
    };

    const mode = ref("create");

    const updateIndex = ref(null);
    const rewardsListLoading = computed(() => root.$store.state.punchCard.rewardsListLoading || false);
    const rewardsListPagination = computed(() => root.$store.state.punchCard.rewardsListPagination || {});

    const getPunchCardRewardsList = (page = 1) => {
      root.$store.dispatch("punchCard/getPunchCardRewardsList", { page });
    };

    const loadMoreRewards = async () => {
      if (!rewardsListLoading.value && rewardsListPagination.value.current < rewardsListPagination.value.max_page)
        await getPunchCardRewardsList(rewardsListPagination.value.current + 1);
    };

    const controlValue = (field, direction, refI) => {
      const { min, max } = currentInstance.proxy.$refs[refI];
      let valNum = Number(form.value[field]);

      if (direction === "up" && valNum < max) {
        form.value[field]++;
      } else if (direction === "down" && valNum >= min) {
        form.value[field]--;
      }
    };

    onMounted(() => {
      root.$on("bv::show::modal", (id, index) => {
        if (id === "modal-punch-card-campaign-reward-modal" && index !== undefined) {
          form.value = { ...currentInstance.proxy.campaignData.design.milestones[index] };
          mode.value = "update";
          updateIndex.value = index;
        } else if (id === "modal-punch-card-campaign-reward-modal" && !index) {
          mode.value = "create";
          updateIndex.value = null;
        }
      });
    });

    const modalOpen = ref(false);

    const clearModal = () => {
      modalOpen.value = false;
      resetFormValue();
      document.body.classList.remove("modal-open");
    };

    return {
      maxLengths,
      form,
      validateForm,
      $v,
      setImage,
      clearImage,
      resetImageUploader,
      setWarningMessage,
      uploadedImgWarning,
      initForm,
      mode,
      punchCardRewardsList,
      loadMoreRewards,
      rewardsListLoading,
      rewardsListPagination,
      controlValue,
      clearModal
    }
  }
});
</script>

<style lang="scss" scoped>
#input-punch-card-campaign-reward-stamp-count {
  min-width: 100px;
}

.input-controls {
  right: 15px;
  justify-content: center;

  &>.btn {
    padding: 0;
    width: 9px;
    height: 6.5px;
    background: transparent url('~@/assets/images/spinner-control.png') no-repeat 100%;
    border: 0;
    box-shadow: none;
    margin: 1px 0;

    &:active,
    &:focus:active {
      background: transparent url('~@/assets/images/spinner-control.png') no-repeat 100%;
      box-shadow: none;
    }

    &.-down {
      transform: rotate(180deg);
    }
  }
}
</style>

<style lang="scss">
#modal-punch-card-campaign-reward-modal {
  &>.modal-dialog>.modal-content {
    &>.modal-header {
      &>.modal-title {
        text-align: left;
        width: 100%;
        padding-left: 18px;
        font-family: MainFontBold, sans-serif;
      }
    }

    &>.modal-body {
      padding-left: 32px;
      padding-right: 32px;
    }

    &>.modal-footer {
      button {
        width: 183px;
        height: 44px;
      }
    }
  }
}

#dropdown-punch-card-campaign-rewards {
  .dropdown-toggle {
    height: 44px;
    background: transparent url("~@/assets/images/caret-down-2.svg") no-repeat right 12px center;
    padding-right: 2.75rem;
    border-color: #D8DCDE;

    &.unselected {
      color: #87929D !important;
    }
  }

  .dropdown-menu {
    max-height: 300px;
    background: #fff;
    overflow-y: auto;
  }
}
</style>
